import * as _ from 'lodash';

/**
 * Represents the `skip` and `take` configurations which are wrapped in the `Page` object.
 */
export interface Page {
  /**
   * The number of records that will be skipped.
   */
  skip: number;

  /**
   * The number of records that will be taken.
   */
  take: number;
}
/**
 * The type of the GridRow component.
 *
 * The available values are:
 * - `groupHeader`&mdash;The row is a group header.
 * - `groupFooter`&mdash;The row is a group footer.
 * - `data`&mdash;The row corresponds to an item from the `data` collection which is passed to the Grid.
 */
export type GridRowType = 'groupFooter' | 'groupHeader' | 'data';

import { SyntheticEvent, RefObject } from 'react';
import * as React from 'react';
import { insert } from '../../utility';
/**
 * @hidden
 */
export interface DataItemWrapper {
  dataItem: any;
  rowType: GridRowType;
  level: number;
  expanded: boolean;
  dataIndex: number;
}
/**
 * @hidden
 */
export const firefox = typeof window !== 'undefined' &&
  /Firefox/.test(window.navigator.userAgent);

/**
 * @hidden
 */
export class RowHeightService {
  public offsets: Array<number> = [];
  public heights: Array<number> = [];
  isCollapsedAll: boolean = false;
  isExpandedAll: boolean = false;
  vs: any;

  constructor(private total: number = 0, rowHeight: number, detailRowHeight: number, data?: DataItemWrapper[], grid?: any) {
    let agg = 0;
    this.isCollapsedAll = grid?.isCollapsedAll;
    this.isExpandedAll = grid?.isExpandedAll;
    this.vs = grid?.gridRef?.vs;
    for (let idx = 0; idx < total; idx++) {
      this.offsets.push(agg);
      const currHeight = (data && data[idx].expanded && data[idx].rowType === 'data') ? detailRowHeight : rowHeight;
      agg += currHeight;
      this.heights.push(currHeight);
    }
  }

  /**
   * Sets corrected row heights
   * @param rows
   * @param prevSkip
   * @param skip
   */
  public setHeights = (rows: any, prevSkip?: number, skip?: number): number => {
    let isCorrScrollY: boolean = false;
    let corrScrollY: number = 0;
    if ((prevSkip !== undefined && skip !== undefined && prevSkip > skip) || this.isCollapsedAll || this.isExpandedAll) {
      isCorrScrollY = true;
    }
    let firstRowIndex: string | number | null | undefined = rows?.[0]?.getAttribute('aria-rowindex');
    let firstRowUnderContainerIndex;
    if (typeof firstRowIndex === 'string') {
      firstRowIndex = parseInt(firstRowIndex) - 2;
      if ((this.isCollapsedAll || this.isExpandedAll) && this.vs?.container) {
        firstRowUnderContainerIndex = this.index(this.vs.container.offsetHeight + parseInt(this.vs.container.scrollTop));
      }
      const total = this.heights.length;
      let agg = firstRowIndex > 0 ? this.heights[firstRowIndex - 1] + this.offsets[firstRowIndex - 1] : 0;
      let rowsCounter = 0;
      let currentHeight;
      let currentRealRowHeight;
      let isTreeItem = false;
      let corrScrollYCondition;
      for (let idx = firstRowIndex; idx < total; idx++) {
        this.offsets[idx] = agg;
        isTreeItem = rows[rowsCounter]?.getAttribute('class').search('tp-row-expanded') !== -1;
        currentRealRowHeight = !isTreeItem ? rows[rowsCounter]?.offsetHeight : rows[rowsCounter]?.offsetHeight + rows[rowsCounter + 1]?.offsetHeight;
        currentHeight = !currentRealRowHeight ? this.heights[idx] : currentRealRowHeight;
        agg += currentHeight;
        if (this.isCollapsedAll || this.isExpandedAll) {
          corrScrollYCondition = isCorrScrollY && prevSkip !== undefined && skip !== undefined && idx <= firstRowUnderContainerIndex;
        } else {
          corrScrollYCondition = isCorrScrollY && prevSkip !== undefined && skip !== undefined && idx >= skip && idx <= prevSkip;
        }
        if (corrScrollYCondition) {
          corrScrollY += currentHeight - this.heights[idx];
        }
        this.heights[idx] = currentHeight;
        if (isTreeItem) {
          rowsCounter += 2;
        } else {
          rowsCounter++;
        }
      }
    }
    return corrScrollY;
  }

  public height(rowIndex: number): number {
    return this.heights[rowIndex];
  }

  public index(position: number): number | undefined {
    if (position < 0) {
      return undefined;
    }

    const result: number | undefined = this.offsets.reduce((prev: number | undefined, current: number, idx: number) => {
      if (prev !== undefined) {
        return prev;
      } else if (current === position) {
        return idx;
      } else if (current > position) {
        return idx - 1;
      }
      return undefined;
    }, undefined);
    // console.log('INDEX', result, position, [...this.offsets]);
    return result === undefined ? this.total - 1 : result;
  }

  public offset(rowIndex: number): number {
    return this.offsets[rowIndex];
  }

  public totalHeight(): number {
    const lastOffset = this.offsets[this.offsets.length - 1];
    const lastHeight = this.heights[this.heights.length - 1];
    return lastOffset + lastHeight;
    // return this.heights.reduce((prev, curr) => prev + curr, 0);
  }
}

/**
 * @hidden
 */
export class ScrollCorrectionServiceVS {
  fakeScrollTop: number = -1000;
  rowHeights: Array<number> = [];
  rowOffsets: Array<number> = [];
  grid: any;
  scrollStop: any;
  isScrollStopped: boolean = false;
  skip: number = 0;
  prevSkip: number = 0;
  data: any[] = [];
  rowHeight: number = 41;

  private setScrollHandler = (scope) => {
    const me = this;
    scope.scrollHandler = (e: React.SyntheticEvent<HTMLDivElement>) => {
      // me.isScrollStopped = false;
      // clearTimeout(me.scrollStop);
      if (!scope.scrollableVirtual || !scope.container || !scope.table ||
        !scope.rowHeightService || !scope.containerRef.current) {
        return;
      }

      if (scope.scrollSyncing) {
        scope.scrollSyncing = false;
        return;
      }

      const scrollTop = scope.container.scrollTop;
      // if (scrollTop !== me.fakeScrollTop) {
      this.setRowHeights(scope, this.prevSkip, this.skip, this.data, this.rowHeight);
      const up = scope.lastScrollTop >= scrollTop;
      const down = !up;
      scope.lastScrollTop = scrollTop;

      let firstItemIndex = scope.rowHeightService.index(scrollTop)!;
      let firstItemOffset = scope.rowHeightService.offset(firstItemIndex);

      const { offsetHeight } = scope.containerRef.current;

      const lastItemIndex = scope.rowHeightService.index(scrollTop + offsetHeight)!;

      if (down && lastItemIndex >= scope.lastLoaded && scope.lastLoaded < scope.total) {
        const overflow = (firstItemIndex + scope.pageSize) - scope.total;

        if (overflow > 0) {
          firstItemIndex = firstItemIndex - overflow;
          firstItemOffset = scope.rowHeightService.offset(firstItemIndex);
        }

        scope.firstLoaded = firstItemIndex;
        scope.translate(firstItemOffset);

        let nextTake = scope.firstLoaded + scope.pageSize;
        scope.lastLoaded = Math.min(nextTake, scope.total);

        scope.changePage(scope.firstLoaded, e);
      } else if (up && firstItemIndex < scope.firstLoaded) {
        const nonVisibleBuffer = Math.floor(scope.pageSize * 0.3);

        scope.firstLoaded = Math.max(firstItemIndex - nonVisibleBuffer, 0);
        scope.translate(scope.rowHeightService.offset(scope.firstLoaded));

        scope.lastLoaded = Math.min(scope.firstLoaded + scope.pageSize, scope.total);
        scope.changePage(scope.firstLoaded, e);
      }
      // }
      // me.fakeScrollTop = -1000;
      // me.scrollStop = setTimeout(() => {
      //   me.isScrollStopped = true;
      //   me.forceRenderVS(scope);
      // }, 1000);
    };
  };

  forceRenderVS = (scope) => {
    const scrollTop = scope.container.scrollTop;

    scope.lastScrollTop = scrollTop;

    let firstItemIndex = scope.rowHeightService.index(scrollTop)!;
    if (firstItemIndex !== -1) {
      let firstItemOffset = scope.rowHeightService.offset(firstItemIndex);

      const { offsetHeight } = scope.containerRef.current;

      const lastItemIndex = scope.rowHeightService.index(scrollTop + offsetHeight)!;

      if (lastItemIndex >= scope.lastLoaded && scope.lastLoaded < scope.total) {
        const overflow = (firstItemIndex + scope.pageSize) - scope.total;

        if (overflow > 0) {
          firstItemIndex = firstItemIndex - overflow;
          firstItemOffset = scope.rowHeightService.offset(firstItemIndex);
        }

        scope.firstLoaded = firstItemIndex;
        scope.translate(firstItemOffset);

        let nextTake = scope.firstLoaded + scope.pageSize;
        scope.lastLoaded = Math.min(nextTake, scope.total);

        scope.changePage(scope.firstLoaded, {});
      } else if (firstItemIndex < scope.firstLoaded) {
        const nonVisibleBuffer = Math.floor(scope.pageSize * 0.3);

        scope.firstLoaded = Math.max(firstItemIndex - nonVisibleBuffer, 0);
        scope.translate(scope.rowHeightService.offset(scope.firstLoaded));

        scope.lastLoaded = Math.min(scope.firstLoaded + scope.pageSize, scope.total);
        scope.changePage(scope.firstLoaded, {});
      }
    }
  }

  /**
   * Sets RowHeightService and other configuration
   * @param scope
   * @param prevSkip
   * @param skip
   * @param data
   * @param rowHeight
   */
  private setRowHeights = (scope: any, prevSkip: number, skip: number, data: any[], rowHeight: number): boolean => {
    scope.rowHeightService = new RowHeightService(data.length, rowHeight, rowHeight, data, this.grid);
    if (!_.isEmpty(this.rowHeights) && scope.rowHeightService.heights.length === this.rowHeights.length) {
      scope.rowHeightService.heights = [...this.rowHeights];
      scope.rowHeightService.offsets = [...this.rowOffsets];
    }
    const corrScrollY = scope.rowHeightService?.setHeights(scope.table?.childNodes?.[1]?.childNodes, prevSkip, skip);
    if (_.isEqual([...this.rowHeights], [...scope.rowHeightService?.heights])) {
      return true;
    }
    this.rowHeights = [...scope.rowHeightService?.heights];
    this.rowOffsets = [...scope.rowHeightService?.offsets];
    scope.containerHeight = scope.rowHeightService?.totalHeight ? scope.rowHeightService?.totalHeight() : scope.containerHeight;
    const el: any = scope.container?.querySelector('.k-height-container'); // .style.height = `${scope.containerHeight}px`;
    if (el) {
      el.style.height = `${scope.containerHeight}px`;
    }
    if (scope.container.scrollTop !== 0 && corrScrollY !== 0 && corrScrollY !== undefined && scope.container) {
      scope.container.scrollTop += corrScrollY;
      this.fakeScrollTop = scope.container.scrollTop;
    }
    return false;
  };

  /**
   * Inserts new rows by client side
   * @param index
   * @param num
   */
  public setNewClientRows = (index, num) => {
    this.setFixedScroll(true);
    let addedRowHeights = [];
    for (let i = 0; i < num; i++) {
      addedRowHeights.push(this.rowHeight);
    }
    insert(this.rowHeights, index, addedRowHeights);
    let addedRowOffsets = [];
    const offsetFirst = this.rowOffsets[index];
    for (let i = 0; i < num; i++) {
      addedRowOffsets.push(offsetFirst + (i * this.rowHeight));
    }
    insert(this.rowOffsets, index, addedRowOffsets);
    const addedOffset = num * this.rowHeight;
    for (let i = index + num; i < this.rowOffsets.length; i++) {
      this.rowOffsets[i] += addedOffset;
    }
  }

  /**
   * Sets fixedScroll property in order to not reset scroll position via inserting rows
   * @param fixedScroll
   */
  public setFixedScroll = (fixedScroll) => {
    _.set(this, 'grid.gridRef.vs.fixedScroll', fixedScroll);
  }

  /**
   * Sets correction configuration
   * @param scope
   * @param prevSkip
   * @param skip
   * @param data
   * @param rowHeight
   * @param grid
   */
  public setCorrection = (scope: any, prevSkip: number, skip: number, data: any[], rowHeight: number, grid: any): boolean => {
    skip = _.isUndefined(skip) ? this.skip : skip;
    prevSkip = _.isUndefined(prevSkip) ? this.prevSkip : prevSkip;
    this.grid = grid;
    this.skip = skip;
    this.prevSkip = prevSkip;
    this.data = data;
    this.rowHeight = rowHeight;
    this.setScrollHandler(scope);
    return this.setRowHeights(scope, prevSkip, skip, data, rowHeight);
  }
}
